/**
 * Sentry — observabilidade de erros do front-end (web-client).
 *
 * Inicializacao no entry point (main.tsx). Se VITE_SENTRY_DSN estiver vazio
 * o init é um no-op (úteis para dev local sem ruido).
 *
 * Tags importantes:
 *   - tenant: cod_tenant resolvido pelo subdominio
 *   - role: app_metadata.role do cliente
 *
 * Privacidade (LGPD):
 *   - replaysIntegration mascara texto e bloqueia midia
 *   - beforeSend remove email/username do user
 *   - Respeita CookieBanner (Sprint AAAA): se analytics off, no-op
 *
 * Sprint BBBB.
 */
import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = (import.meta.env.MODE ?? 'development') as string;
const RELEASE = (import.meta.env.VITE_RELEASE ?? 'dev') as string;

const LS_CONSENT_KEY = 'octawash:cookie-consent';

function analyticsConsented(): boolean {
  try {
    const raw = localStorage.getItem(LS_CONSENT_KEY);
    if (!raw) return false; // sem consent => off por default
    const c = JSON.parse(raw) as { analytics?: boolean };
    return c.analytics === true;
  } catch {
    return false;
  }
}

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  if (!DSN) {
    if (ENV !== 'test') {
      console.info('[sentry-client] VITE_SENTRY_DSN ausente — telemetria desativada');
    }
    return;
  }
  if (!analyticsConsented()) {
    if (ENV !== 'test') {
      console.info('[sentry-client] consentimento analytics não dado — telemetria desativada');
    }
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    tracesSampleRate: ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: ENV === 'production' ? 0.1 : 0.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    beforeSend(event) {
      if (event.user) {
        delete event.user.email;
        delete event.user.username;
      }
      return event;
    },
    ignoreErrors: ['ResizeObserver loop limit exceeded', 'Network request failed'],
  });
  initialized = true;
}

export function setSentryUser(params: {
  cod_auth_user: string;
  cod_tenant: string;
  role: string;
}): void {
  if (!initialized) return;
  Sentry.setUser({ id: params.cod_auth_user });
  Sentry.setTag('tenant', params.cod_tenant);
  Sentry.setTag('role', params.role);
}

export function clearSentryUser(): void {
  if (!initialized) return;
  Sentry.setUser(null);
  Sentry.setTag('tenant', undefined);
  Sentry.setTag('role', undefined);
}

/**
 * Re-tenta init quando consentimento muda. Usado por listener de
 * 'octawash:consent-changed' (CookieBanner).
 */
export function maybeInitOnConsent(): void {
  if (initialized) return;
  if (analyticsConsented()) initSentry();
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
